import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";

const PRELOADER_STORAGE_KEY = "mfour-preloader-shown";

export default function Preloader() {
  const [active, setActive] = useState(false);
  const [progress, setProgress] = useState(0);

  // Show only on first visit per browser (persisted via localStorage)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const hasShown = localStorage.getItem(PRELOADER_STORAGE_KEY) === "1";
    if (hasShown) return;

    setProgress(0);
    setActive(true);
  }, []);

  useEffect(() => {
    if (!active) return;

    const timer = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(timer);
          setTimeout(() => {
            setActive(false);
            try {
              localStorage.setItem(PRELOADER_STORAGE_KEY, "1");
            } catch (err) {
              // Ignore storage errors (e.g., private mode)
            }
          }, 500);
          return 100;
        }
        return prev + 2;
      });
    }, 10);

    return () => clearInterval(timer);
  }, [active]);

  return (
    <AnimatePresence>
      {active && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{
            opacity: 0,
            transition: { duration: 0.8, ease: [0.76, 0, 0.24, 1] },
          }}
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-[#050000]"
        >
          {/* Noise Texture */}
          <div className="absolute inset-0 bg-[url('/noise.png')] opacity-[0.03] pointer-events-none" />

          <div className="relative mb-12">
            {/* Logo Silhouette Animation */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: [0.1, 0.5, 0.1], scale: [0.95, 1, 0.95] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              className="absolute inset-0 blur-3xl bg-primary/20 rounded-full"
            />

            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              className="relative flex flex-col items-center"
              dir="ltr"
            >
              <div className="flex items-baseline gap-1 text-white select-none">
                <span className="text-6xl md:text-8xl font-black italic tracking-tighter">
                  M
                </span>
                <span className="text-4xl md:text-6xl font-black italic tracking-tighter text-primary">
                  FOUR
                </span>
              </div>
              <div className="mt-2 text-primary font-mono text-[10px] tracking-[0.5em] uppercase opacity-60">
                Premium Auto Finish
              </div>
            </motion.div>
          </div>

          {/* Progress Section */}
          <div className="relative w-48 h-[1px] bg-white/5 overflow-hidden rounded-full">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              className="absolute inset-y-0 left-0 bg-primary shadow-[0_0_15px_rgba(227,30,36,0.8)]"
            />
          </div>

          <motion.div className="mt-4 font-black text-[10px] text-gray-500 tracking-[0.2em]">
            جاري تجهيز النظام • {progress}%
          </motion.div>

          {/* Bottom Decoration */}
          <div className="absolute bottom-12 left-12 right-12 flex justify-between items-center overflow-hidden">
            <div className="h-[1px] flex-1 bg-white/5" />
            <div className="px-6 font-black text-[9px] text-white/20 uppercase tracking-[0.3em]">
              الأداء • الفخامة • الأناقة
            </div>
            <div className="h-[1px] flex-1 bg-white/5" />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
